import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';

export default function CookiePolicy() {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="max-w-4xl mx-auto px-4 py-12 flex-1">
        <Button 
          onClick={() => navigate('/')} 
          className="mb-8 bg-black text-white hover:bg-gray-800"
        >
          ← Back to Home
        </Button>

        <h1 className="text-4xl font-bold text-black mb-8">Cookie Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: November 14, 2025</p>

        <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">1. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">2. How We Use Cookies</h2>
            <p>
              Chatity uses cookies for the following purposes:
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-black mt-6 mb-3">2.1 Essential Cookies (Required)</h3>
            <p>
              These cookies are necessary for the website to function properly. They enable core functionality such as:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Authentication:</strong> Keeping you logged in to your account</li>
              <li><strong>Security:</strong> Protecting against fraudulent activity</li>
              <li><strong>Session Management:</strong> Remembering your current session</li>
            </ul>
            <p className="mt-4">
              <em>You cannot opt out of essential cookies as they are required for the Service to work.</em>
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-black mt-6 mb-3">2.2 Preference Cookies</h3>
            <p>
              These cookies remember your choices and preferences:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Language Selection:</strong> Your preferred interface language (EN, RU, FR)</li>
              <li><strong>Theme Preferences:</strong> Display settings and customizations</li>
              <li><strong>Cookie Consent:</strong> Your cookie acceptance status</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-black mt-6 mb-3">2.3 Analytics Cookies (Optional)</h3>
            <p>
              We use analytics cookies to understand how you interact with our Service:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Pages visited and features used</li>
              <li>Time spent on the platform</li>
              <li>Performance metrics and error tracking</li>
            </ul>
            <p className="mt-4">
              <em>These cookies help us improve the Service but are not essential.</em>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">3. Specific Cookies We Use</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300 mt-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 border-b font-bold text-left">Cookie Name</th>
                    <th className="px-4 py-2 border-b font-bold text-left">Purpose</th>
                    <th className="px-4 py-2 border-b font-bold text-left">Duration</th>
                    <th className="px-4 py-2 border-b font-bold text-left">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border-b"><code>token</code></td>
                    <td className="px-4 py-2 border-b">Authentication JWT token</td>
                    <td className="px-4 py-2 border-b">7 days</td>
                    <td className="px-4 py-2 border-b">Essential</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b"><code>user</code></td>
                    <td className="px-4 py-2 border-b">User profile data</td>
                    <td className="px-4 py-2 border-b">7 days</td>
                    <td className="px-4 py-2 border-b">Essential</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b"><code>language</code></td>
                    <td className="px-4 py-2 border-b">Preferred interface language</td>
                    <td className="px-4 py-2 border-b">1 year</td>
                    <td className="px-4 py-2 border-b">Preference</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b"><code>cookie-consent</code></td>
                    <td className="px-4 py-2 border-b">Cookie acceptance status</td>
                    <td className="px-4 py-2 border-b">1 year</td>
                    <td className="px-4 py-2 border-b">Preference</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">4. Third-Party Cookies</h2>
            <p>
              Some cookies are placed by third-party services we use:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Stripe:</strong> Payment processing and fraud detection</li>
              <li><strong>Google (Gemini AI):</strong> AI chatbot functionality</li>
            </ul>
            <p className="mt-4">
              These third parties have their own privacy and cookie policies. We recommend reviewing them:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Stripe Privacy Policy: <a href="https://stripe.com/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a></li>
              <li>Google Privacy Policy: <a href="https://policies.google.com/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">5. Managing Your Cookie Preferences</h2>
            
            <h3 className="text-xl font-semibold text-black mt-6 mb-3">5.1 Through Our Banner</h3>
            <p>
              When you first visit our website, you'll see a cookie consent banner. You can:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Accept all cookies</li>
              <li>Decline optional cookies (essential cookies will still be used)</li>
            </ul>

            <h3 className="text-xl font-semibold text-black mt-6 mb-3">5.2 Through Your Browser</h3>
            <p>
              Most browsers allow you to control cookies through their settings. You can:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Block all cookies</li>
              <li>Delete existing cookies</li>
              <li>Allow cookies only from specific websites</li>
            </ul>
            <p className="mt-4">
              Note: Blocking essential cookies may prevent you from using certain features of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">6. Browser-Specific Cookie Controls</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
              <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">7. Do Not Track Signals</h2>
            <p>
              We currently do not respond to Do Not Track (DNT) browser signals. However, you can control cookies as described above.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">8. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy to reflect changes in our practices or legal requirements. We will notify you of significant changes through the Service or via email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mt-8 mb-4">9. Contact Us</h2>
            <p>
              If you have questions about our use of cookies, please contact us at:
            </p>
            <div className="mt-4 space-y-2">
              <p><strong>Email:</strong> support@chatity.io</p>
              <p><strong>Company:</strong> MINDUNITE</p>
              <p><strong>Legal Form:</strong> SAS, société par actions simplifiée</p>
              <p><strong>SIREN:</strong> 990 299 414</p>
              <p><strong>SIRET:</strong> 990 299 414 00013</p>
              <p><strong>VAT Number:</strong> FR05 990 299 414</p>
              <p><strong>Address:</strong> 7 Avenue de Fabron, 06200 Nice, France</p>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
